import React from "react";
import { formatAdjustmentName } from "../../../MortgageCalculators/adjustmentName";
import { formatMoney, formatPrice, formatRate } from "./resultModel";
// The .ppe-kv table and the section title it shares with the closing cost.
import "./RateDetail.css";

// "+" is good for the borrower (price up, credit), "−" a cost; one sign
// convention for every line (plan D27). Points are positive = cost.
const signed = (pricePoints) => {
  const v = Number(pricePoints) || 0;
  if (Math.abs(v) < 0.0005) return "0.000";
  return `${v > 0 ? "+" : "−"}${Math.abs(v).toFixed(3)}`;
};
const signedMoney = (dollarsGood) => {
  const v = Number(dollarsGood) || 0;
  if (Math.round(v) === 0) return "$0";
  return `${v > 0 ? "+" : "−"}${formatMoney(Math.abs(v))}`;
};

// The numbers behind a rate's breakdown: the lock-day points, the listed
// adjustments, what the listed ones do not explain, and the close-over point.
function breakdownParts(product, row) {
  const prices = row.raw.prices || {};
  const adjustments = Object.entries(product.adjustments || {}).filter(([, v]) => Number(v) !== 0);
  const listed = adjustments.reduce((sum, [, v]) => sum + Number(v), 0);
  return {
    lockPoints: Number(prices.lockDayPoint) || 0,
    adjustments,
    other: (Number(prices.adjustmentPoint) || 0) - listed,
    closeOver: Number(prices.closeOverPoint) || 0,
  };
}

// Which optional lines a rate's breakdown has. Rates of one product with the
// same shape take the same height (the adjustments are the product's), so the
// results table reserves one height per shape and does not jump on hover.
export function breakdownShape(product, row) {
  const { adjustments, other, closeOver } = breakdownParts(product, row);
  const hasOther = Math.abs(other) >= 0.0005;
  return [adjustments.length === 0 && !hasOther, hasOther, Boolean(row.capped), closeOver !== 0].join(",");
}

// One rate's price breakdown: base price, each pricing adjustment, price,
// compensation, final price, as price and dollars (part of the rate detail
// beside a product's rates). `actions` sits right of the title.
export default function PriceBreakdown({ product, row, loanAmount, lockDays, compensationType, actions }) {
  const { lockPoints, adjustments, other, closeOver } = breakdownParts(product, row);
  const amount = (points) => -points * loanAmount / 100;
  const lenderPaid = compensationType !== "borrower_paid";
  return (
    <section className="ppe-detail-section" aria-label={`Price breakdown at ${formatRate(row.rate)}`}>
      <div className="ppe-detail-section-title">
        <h3>Price breakdown</h3>
        <span className="ppe-detail-section-sub">at {formatRate(row.rate)}</span>
        <span className="ppe-spacer" />
        {actions}
      </div>
      <table className="ppe-kv">
        <colgroup>
          <col />
          <col className="ppe-kv-col-price" />
          <col className="ppe-kv-col-amount" />
        </colgroup>
        <thead>
          <tr><th scope="col">Item</th><th scope="col" className="r">Price</th><th scope="col" className="r">Amount</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Base price ({row.raw.lockDay || lockDays || 30}-day lock)</td>
            <td className="r">{formatPrice(100 - lockPoints)}</td>
            <td className="r">{signedMoney(amount(lockPoints))}</td>
          </tr>
          <tr className="ppe-kv-head"><td colSpan={3}>Pricing adjustments</td></tr>
          {adjustments.length === 0 && Math.abs(other) < 0.0005 && (
            <tr className="ppe-kv-sub"><td colSpan={3}>None</td></tr>
          )}
          {adjustments.map(([key, value]) => (
            <tr className="ppe-kv-sub" key={key}>
              <td>{formatAdjustmentName(key)}</td>
              <td className={`r ${Number(value) < 0 ? "ppe-good" : "ppe-bad"}`}>{signed(-value)}</td>
              <td className="r">{signedMoney(amount(Number(value)))}</td>
            </tr>
          ))}
          {Math.abs(other) >= 0.0005 && (
            <tr className="ppe-kv-sub">
              <td>Other adjustments</td>
              <td className={`r ${other < 0 ? "ppe-good" : "ppe-bad"}`}>{signed(-other)}</td>
              <td className="r">{signedMoney(amount(other))}</td>
            </tr>
          )}
          <tr className="ppe-kv-total">
            <td>Price</td>
            <td className="r">{formatPrice(row.price)}</td>
            <td className="r">{signedMoney(-row.priceAmount)}</td>
          </tr>
          {row.capped && (
            <tr className="ppe-kv-sub"><td colSpan={3}>The lender's maximum price is {formatPrice(row.capPrice)}; this rate is capped at it.</td></tr>
          )}
          <tr>
            <td>Comp ({lenderPaid ? "lender paid" : "borrower paid"})</td>
            {product.isConnected && lenderPaid ? (
              <>
                <td className={`r ${row.comp ? "ppe-bad" : ""}`}>{signed(-(row.comp || 0))}</td>
                <td className="r">{signedMoney(-(row.compAmount || 0))}</td>
              </>
            ) : (
              <td className="r" colSpan={2}>{product.isConnected ? "paid at closing" : "not added for lenders you have not connected"}</td>
            )}
          </tr>
          {closeOver !== 0 && (
            <tr>
              <td>Close over point</td>
              <td className="r">{signed(-closeOver)}</td>
              <td className="r">{signedMoney(amount(closeOver))}</td>
            </tr>
          )}
          <tr className="ppe-kv-big">
            <td>Final price</td>
            <td className={`r ${row.final >= 100 ? "ppe-good" : ""}`}>{formatPrice(row.final)}</td>
            <td className="r">{signedMoney(-row.finalAmount)}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}
